import React, { useId } from 'react';
import { FormControl, InputLabel, ListSubheader, MenuItem, Select } from '@mui/material';
import PropTypes from 'prop-types';
import { PALETTE, alpha} from '../theme';

/**
 * Outlined Select with a floating MUI label — the single dropdown component
 * used everywhere in the app so every dropdown looks identical.
 *
 * Pass either:
 *   - `options`: a flat array of `{ value, text }`
 *   - `groups`:  an array of `{ label, items: [{ value, text }] }` for
 *                section-grouped dropdowns (the Travel Map's map picker uses
 *                this).
 *
 * Both render the same outlined input with floating label so the user sees
 * a single, consistent dropdown style across the app.
 */
const SelectDropdown = ({
    value,
    onChange,
    options,
    groups,
    label,
    disabled = false,
    minWidth = { xs: '140px', sm: '160px' },
    maxWidth
}) => {
    const labelId = useId();

    // Resolve the visible text for the selected value, looking in both
    // flat options and grouped items.
    const findOption = (selectedValue) => {
        if (options) return options.find((o) => o.value === selectedValue);
        if (groups) {
            for (const g of groups) {
                const hit = g.items.find((o) => o.value === selectedValue);
                if (hit) return hit;
            }
        }
        return null;
    };

    return (
        <FormControl
            sx={{
                minWidth,
                maxWidth,
                width: { xs: '100%', sm: 'auto' },
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#ffffff'
                },
                '& .MuiOutlinedInput-input': {
                    color: PALETTE.text,
                    fontWeight: 700,
                    fontSize: '1.05rem'
                }
            }}
        >
            <InputLabel id={labelId} shrink={value !== undefined && value !== '' ? true : undefined}>
                {label}
            </InputLabel>
            <Select
                labelId={labelId}
                label={label}
                value={value ?? ''}
                onChange={onChange}
                disabled={disabled}
                notched={value !== undefined && value !== '' ? true : undefined}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            borderRadius: 2,
                            boxShadow: `0 4px 20px ${alpha('text', 0.12)}`,
                            mt: 1,
                            maxHeight: 360,
                            backgroundColor: '#ffffff'
                        }
                    }
                }}
                renderValue={(selected) => {
                    if (selected === '' || selected === undefined || selected === null) return '';
                    const opt = findOption(selected);
                    return opt ? opt.text : '';
                }}
            >
                {options &&
                    options.map((option) => (
                        <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={menuItemSx}
                        >
                            {option.text}
                        </MenuItem>
                    ))}
                {groups &&
                    groups.flatMap((group) => [
                        <ListSubheader
                            key={`group-${group.label}`}
                            sx={{
                                fontWeight: 700,
                                color: PALETTE.textBody,
                                backgroundColor: '#f8fafc',
                                fontSize: '0.75rem',
                                textTransform: 'uppercase',
                                letterSpacing: 1,
                                lineHeight: '32px'
                            }}
                        >
                            {group.label}
                        </ListSubheader>,
                        ...group.items.map((item) => (
                            <MenuItem
                                key={item.value}
                                value={item.value}
                                sx={{ ...menuItemSx, pl: 4 }}
                            >
                                {item.text}
                            </MenuItem>
                        ))
                    ])}
            </Select>
        </FormControl>
    );
};

const menuItemSx = {
    color: PALETTE.text,
    fontWeight: 600,
    fontSize: '1.05rem',
    padding: '12px 16px',
    minHeight: '48px',
    backgroundColor: '#ffffff',
    '&:hover': { backgroundColor: '#f1f5f9' },
    '&.Mui-selected': {
        backgroundColor: '#e0ecff',
        color: PALETTE.text,
        fontWeight: 700,
        '&:hover': { backgroundColor: '#cee0ff' }
    }
};

SelectDropdown.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onChange: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            text: PropTypes.string.isRequired
        })
    ),
    groups: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string.isRequired,
            items: PropTypes.arrayOf(
                PropTypes.shape({
                    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                    text: PropTypes.string.isRequired
                })
            ).isRequired
        })
    ),
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    minWidth: PropTypes.object,
    maxWidth: PropTypes.object
};

export default SelectDropdown;
